/**
 * Atajos de la bandeja.
 *
 * La bandeja se usa **muchas horas por día**: un investigador de brand
 * protection pasa el turno entero acá. Que se pueda triar sin soltar el
 * teclado no es una comodidad, es la diferencia entre revisar cuarenta casos
 * por hora y quince. El criterio de aceptación de #42 lo dice literal: se
 * tiene que poder resolver un caso sin tocar el mouse.
 *
 * La resolución de teclas vive acá, pura, por dos motivos: se prueba sin
 * montar el DOM, y así queda un único lugar donde está escrito qué hace cada
 * tecla —el `switch` disperso en tres componentes es como aparecen dos
 * atajos que hacen lo mismo.
 */

export type InboxAction =
  | 'next'
  | 'previous'
  | 'first'
  | 'last'
  | 'assignSelf'
  | 'unassign'
  | 'investigate'
  | 'confirm'
  | 'dismiss'
  | 'reopen'
  | 'note'
  | 'refresh'
  | 'help'
  | 'close';

export interface KeyChord {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /** El foco está en un campo de texto: las letras son texto, no atajos. */
  editing: boolean;
}

/**
 * Las teclas que mueven la selección se aceptan también con `j`/`k` (dedos en
 * la fila de inicio) y con las flechas (lo que prueba cualquiera sin leer la
 * ayuda). Las dos formas conviven a propósito.
 */
const BINDINGS: Record<string, InboxAction> = {
  j: 'next',
  ArrowDown: 'next',
  k: 'previous',
  ArrowUp: 'previous',
  Home: 'first',
  End: 'last',
  a: 'assignSelf',
  u: 'unassign',
  i: 'investigate',
  c: 'confirm',
  d: 'dismiss',
  o: 'reopen',
  n: 'note',
  r: 'refresh',
  '?': 'help',
  Escape: 'close',
};

/** Las que siguen andando con el foco dentro de un campo de texto. */
const WHILE_EDITING = new Set<InboxAction>(['close']);

/**
 * `null` si la combinación no es un atajo. Cualquier modificador la descarta:
 * `Ctrl+D` es "marcador" del navegador y `Cmd+K` es otra cosa en cada
 * sistema; robárselos a la persona es peor que no tener el atajo.
 */
export function resolveChord(chord: KeyChord): InboxAction | null {
  if (chord.ctrlKey || chord.metaKey || chord.altKey) return null;
  const action = BINDINGS[chord.key];
  if (action === undefined) return null;
  if (chord.editing && !WHILE_EDITING.has(action)) return null;
  return action;
}

/** ¿El evento salió de un campo donde la persona está escribiendo? */
export function isEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

/** Acciones que escriben en el caso: un `viewer` no las tiene. */
const WRITE_ACTIONS = new Set<InboxAction>([
  'assignSelf',
  'unassign',
  'investigate',
  'confirm',
  'dismiss',
  'reopen',
  'note',
]);

export function isWriteAction(action: InboxAction): boolean {
  return WRITE_ACTIONS.has(action);
}

/** La ayuda que se muestra con `?`, en orden de uso. */
export const SHORTCUT_HELP: readonly { keys: string; action: InboxAction }[] = [
  { keys: 'j / ↓', action: 'next' },
  { keys: 'k / ↑', action: 'previous' },
  { keys: 'Home / End', action: 'first' },
  { keys: 'a', action: 'assignSelf' },
  { keys: 'u', action: 'unassign' },
  { keys: 'i', action: 'investigate' },
  { keys: 'c', action: 'confirm' },
  { keys: 'd', action: 'dismiss' },
  { keys: 'o', action: 'reopen' },
  { keys: 'n', action: 'note' },
  { keys: 'r', action: 'refresh' },
  { keys: 'Esc', action: 'close' },
];

/**
 * Mueve la selección dentro de la lista. Devuelve el índice nuevo, acotado a
 * los extremos: en una bandeja, "seguir bajando" al final tiene que quedarse
 * en el último caso y no volver al primero — dar la vuelta hace que alguien
 * revise dos veces el mismo caso creyendo que avanzó.
 */
export function moveSelection(
  current: number,
  total: number,
  action: InboxAction,
): number {
  if (total === 0) return -1;
  switch (action) {
    case 'next':
      return current < 0 ? 0 : Math.min(current + 1, total - 1);
    case 'previous':
      return current <= 0 ? 0 : current - 1;
    case 'first':
      return 0;
    case 'last':
      return total - 1;
    default:
      return current;
  }
}
